import type {
  SpreadsheetCellSaveState,
  SpreadsheetCellCoordinate,
  SpreadsheetSelectionRange,
} from './spreadsheetTypes'

export interface SpreadsheetCellRange {
  startRowIndex: number
  endRowIndex: number
  startColumnIndex: number
  endColumnIndex: number
  columnIds: string[]
}

export function cellKey(cell: SpreadsheetCellCoordinate): string {
  return `${cell.rowId}::${cell.columnId}`
}

export function buildSpreadsheetCellRange(
  anchor: SpreadsheetCellCoordinate,
  focus: SpreadsheetCellCoordinate,
  columnIds: string[]
): SpreadsheetCellRange | null {
  const anchorColumnIndex = columnIds.indexOf(anchor.columnId)
  const focusColumnIndex = columnIds.indexOf(focus.columnId)

  if (anchorColumnIndex === -1 || focusColumnIndex === -1) return null

  return {
    startRowIndex: Math.min(anchor.rowIndex, focus.rowIndex),
    endRowIndex: Math.max(anchor.rowIndex, focus.rowIndex),
    startColumnIndex: Math.min(anchorColumnIndex, focusColumnIndex),
    endColumnIndex: Math.max(anchorColumnIndex, focusColumnIndex),
    columnIds,
  }
}

export function isCellInSpreadsheetRange(
  cell: SpreadsheetCellCoordinate,
  range: SpreadsheetCellRange | null
): boolean {
  if (!range) return false

  const columnIndex = range.columnIds.indexOf(cell.columnId)
  return (
    cell.rowIndex >= range.startRowIndex &&
    cell.rowIndex <= range.endRowIndex &&
    columnIndex >= range.startColumnIndex &&
    columnIndex <= range.endColumnIndex
  )
}

export function nextSelectionRangeForCell(
  currentSelection: SpreadsheetSelectionRange | null,
  cell: SpreadsheetCellCoordinate,
  extend: boolean
): SpreadsheetSelectionRange {
  if (extend && currentSelection) {
    return { anchor: currentSelection.anchor, focus: cell }
  }

  return { anchor: cell, focus: cell }
}

export function isCoordinateAlignedWithRows(
  cell: SpreadsheetCellCoordinate,
  rowIds: readonly string[]
): boolean {
  return rowIds[cell.rowIndex] === cell.rowId
}

export function isSelectionAlignedWithRows(
  selection: SpreadsheetSelectionRange | null,
  rowIds: readonly string[]
): boolean {
  if (!selection) return true
  return (
    isCoordinateAlignedWithRows(selection.anchor, rowIds) &&
    isCoordinateAlignedWithRows(selection.focus, rowIds)
  )
}

export function moveCellWithinRows(
  cell: SpreadsheetCellCoordinate,
  rowDelta: number,
  rowIds: string[]
): SpreadsheetCellCoordinate | null {
  const nextRowIndex = cell.rowIndex + rowDelta
  const rowId = rowIds[nextRowIndex]
  if (nextRowIndex < 0 || !rowId) return null

  return {
    rowIndex: nextRowIndex,
    rowId,
    columnId: cell.columnId,
  }
}

export function moveCellWithinColumns(
  cell: SpreadsheetCellCoordinate,
  columnDelta: number,
  columnIds: string[]
): SpreadsheetCellCoordinate | null {
  const currentColumnIndex = columnIds.indexOf(cell.columnId)
  if (currentColumnIndex === -1) return null

  const columnId = columnIds[currentColumnIndex + columnDelta]
  if (!columnId) return null

  return {
    ...cell,
    columnId,
  }
}

export function parseSpreadsheetPaste(text: string): {
  mode: 'single' | 'column' | 'matrix'
  rows: string[][]
  values: string[]
} {
  const normalized = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n')
  const rows = normalized.endsWith('\n')
    ? normalized.slice(0, -1).split('\n')
    : normalized.split('\n')
  const matrix = rows.map((row) => row.split('\t'))
  const values = matrix.map((row) => row[0] ?? '')
  const hasMultipleColumns = matrix.some((row) => row.length > 1)

  return {
    mode:
      values.length <= 1 && !hasMultipleColumns
        ? 'single'
        : hasMultipleColumns
          ? 'matrix'
          : 'column',
    rows: matrix,
    values,
  }
}

export function formatCopyMatrix(rows: string[][]): string {
  return rows.map((row) => row.join('\t')).join('\n')
}

export function shouldClearSelectionOnKey(key: string): boolean {
  return key === 'Backspace' || key === 'Delete'
}

export function shouldWarnBeforeSpreadsheetUnload(
  editing: boolean,
  pendingSaveCount: number
): boolean {
  return editing || pendingSaveCount > 0
}

export function getOptimisticSavingValue(
  saveState: SpreadsheetCellSaveState | undefined
): string | null {
  if (saveState?.status !== 'saving') return null
  return saveState.optimisticValue ?? null
}

export function getSpreadsheetCellPreviewValue({
  editing,
  selected,
  draftValue,
  optimisticSavingValue,
}: {
  editing: boolean
  selected: boolean
  draftValue: string
  optimisticSavingValue: string | null
}): string | null {
  if (editing && selected) return draftValue
  return optimisticSavingValue
}

export function shouldUseDocumentSpreadsheetPaste({
  defaultPrevented,
  editing,
  hasActiveCell,
  targetInsideSpreadsheet,
  targetIsTextEditable,
}: {
  defaultPrevented: boolean
  editing: boolean
  hasActiveCell: boolean
  targetInsideSpreadsheet: boolean
  targetIsTextEditable: boolean
}): boolean {
  return (
    !defaultPrevented &&
    !editing &&
    hasActiveCell &&
    targetInsideSpreadsheet &&
    !targetIsTextEditable
  )
}

export function valuesForPasteTarget(
  pastedValues: string[],
  targetCount: number
): string[] {
  if (targetCount <= 0) return []
  if (pastedValues.length === 1) {
    return Array.from({ length: targetCount }, () => pastedValues[0] ?? '')
  }
  return pastedValues.slice(0, targetCount)
}

export function splitSaveTargetsBySavingKeys<TTarget extends { key: string }>(
  targets: TTarget[],
  savingKeys: ReadonlySet<string>
): {
  readyTargets: TTarget[]
  queuedTargets: TTarget[]
} {
  return targets.reduce<{
    readyTargets: TTarget[]
    queuedTargets: TTarget[]
  }>(
    (acc, target) => {
      if (savingKeys.has(target.key)) {
        acc.queuedTargets.push(target)
      } else {
        acc.readyTargets.push(target)
      }
      return acc
    },
    { readyTargets: [], queuedTargets: [] }
  )
}

export interface SpreadsheetFailedSave {
  key: string
  reason: unknown
}

/**
 * Pick out the targets whose save promise rejected and that have NOT been
 * superseded by a queued mid-flight edit. Targets the user re-edited during
 * the save are excluded — the queued edit will run next and overwrite the
 * stale failure.
 */
export function computeFailedSaves<TTarget extends { key: string }>(
  targets: TTarget[],
  results: PromiseSettledResult<unknown>[],
  queuedNextKeys: ReadonlySet<string>
): SpreadsheetFailedSave[] {
  return results.flatMap((result, index) => {
    const target = targets[index]
    if (
      !target ||
      result.status === 'fulfilled' ||
      queuedNextKeys.has(target.key)
    ) {
      return []
    }
    return [{ key: target.key, reason: result.reason }]
  })
}

/**
 * Compute the next saveStates map after a batch settles. For every target
 * the batch ran:
 *   - if a queued mid-flight edit exists, keep the entry as ``saving`` with
 *     the queued value so the cell does not visually flicker between batches;
 *   - otherwise drop the entry (the cell is no longer pending).
 * Then mark genuinely failed targets as ``error``.
 */
export function applyBatchResultsToSaveStates<
  TTarget extends { key: string; value: string },
>(
  prev: Record<string, SpreadsheetCellSaveState>,
  targets: TTarget[],
  queuedNextByKey: ReadonlyMap<string, TTarget>,
  failed: ReadonlyArray<SpreadsheetFailedSave>
): Record<string, SpreadsheetCellSaveState> {
  const next = { ...prev }
  targets.forEach(({ key }) => {
    const queued = queuedNextByKey.get(key)
    if (queued) {
      next[key] = { status: 'saving', optimisticValue: queued.value }
      return
    }
    delete next[key]
  })
  failed.forEach(({ key, reason }) => {
    next[key] = {
      status: 'error',
      error: reason instanceof Error ? reason.message : 'Failed to save',
    }
  })
  return next
}
